import type { Project } from '@/domain/types';
import type { RecoverySnapshot, StorageProvider } from './StorageProvider';

/**
 * Autosave and crash recovery.
 *
 * Two distinct things are written, and keeping them distinct is the whole
 * design:
 *
 *  - **The saved project** is what the user asked to keep. Only an explicit
 *    save writes it.
 *  - **The recovery snapshot** is a separate record of unsaved work. Autosave
 *    writes here, and only here.
 *
 * Conflating them is the classic autosave bug: the user experiments, the app
 * "helpfully" autosaves over their last good version, and there is no way back.
 * Here, a crash offers to restore the snapshot, and the saved project is
 * untouched until the user says so.
 *
 * Writes are debounced and rate-limited. A write during playback is harmless —
 * IndexedDB work happens off the audio thread — but serialising a large project
 * on every keystroke would still cost main-thread time that the timeline needs.
 */

export interface AutosaveOptions {
  /** Quiet period after the last edit before a snapshot is written. */
  debounceMs?: number;
  /** Longest gap between snapshots while edits keep arriving. */
  maxIntervalMs?: number;
  onError?: (error: unknown) => void;
  onSnapshot?: (savedAt: number) => void;
  /** Injectable clock, so rate limiting can be tested without waiting. */
  now?: () => number;
}

export class AutosaveService {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: Project | null = null;
  private lastWriteAt = 0;
  private writing = false;
  private disposed = false;
  private readonly debounceMs: number;
  private readonly maxIntervalMs: number;
  private readonly now: () => number;

  constructor(
    private readonly storage: StorageProvider,
    private readonly options: AutosaveOptions = {},
  ) {
    this.debounceMs = options.debounceMs ?? 1500;
    this.maxIntervalMs = options.maxIntervalMs ?? 15000;
    this.now = options.now ?? (() => Date.now());
    // The rate-limit clock starts now, so the first edit after loading a
    // project is debounced like any other rather than writing immediately.
    this.lastWriteAt = this.now();
  }

  /** Records that the project changed. Safe to call on every edit. */
  notifyChanged(project: Project): void {
    if (this.disposed) return;
    this.pending = project;

    const sinceLastWrite = this.now() - this.lastWriteAt;
    if (sinceLastWrite >= this.maxIntervalMs) {
      void this.flush();
      return;
    }

    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.debounceMs);
  }

  /** Writes any pending snapshot immediately. Used before unload and on save. */
  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const project = this.pending;
    if (!project || this.writing || this.disposed) return;

    this.writing = true;
    this.pending = null;
    try {
      const savedAt = this.now();
      await this.storage.saveRecovery({
        projectId: project.meta.id,
        projectName: project.meta.name,
        savedAt,
        project,
        superseded: false,
      });
      this.lastWriteAt = savedAt;
      this.options.onSnapshot?.(savedAt);
    } catch (error) {
      // Autosave failures must never interrupt work. They are surfaced through
      // the status bar, not through a modal that steals focus mid-take.
      this.options.onError?.(error);
    } finally {
      this.writing = false;
    }
  }

  /** Called after an explicit save: the snapshot no longer represents unsaved work. */
  async markSaved(): Promise<void> {
    this.pending = null;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    try {
      await this.storage.clearRecovery();
    } catch (error) {
      this.options.onError?.(error);
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.pending = null;
  }
}

export interface RecoveryOffer {
  snapshot: RecoverySnapshot;
  /** True when the recovery snapshot is newer than the stored project. */
  newerThanSaved: boolean;
  savedModifiedAt: number | null;
}

/**
 * Decides whether to offer recovery on startup. A snapshot is only worth
 * offering if it is actually ahead of what was saved; otherwise it is noise
 * from a clean shutdown.
 */
export async function findRecoveryOffer(storage: StorageProvider): Promise<RecoveryOffer | null> {
  const snapshot = await storage.loadRecovery();
  if (!snapshot) return null;

  const saved = await storage.loadProject(snapshot.projectId);
  const savedModifiedAt = saved?.meta.modifiedAt ?? null;
  const newerThanSaved = savedModifiedAt === null || snapshot.savedAt > savedModifiedAt + 1000;

  if (!newerThanSaved) {
    await storage.clearRecovery();
    return null;
  }

  return { snapshot, newerThanSaved, savedModifiedAt };
}
